import React, { useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Platform
} from 'react-native';
import { observer } from '@legendapp/state/react';
import { useRouter } from 'expo-router';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { getLogger } from '../../../utils/logger';
import theme from '../../../config/theme';
import { globalState } from '../../../state/globalState';
import { observable } from '@legendapp/state';
import UpgradePrompt from '@/components/subscription/UpgradePrompt';
import {
  getSettingsContentPaddingBottom,
  getSettingsHeaderPaddingTop,
} from '@/utils/safeArea';
import { hasPremiumAccess, hasDeveloperBypass } from '@/utils/subscriptionAccess';
import {
  canAccessChecklistSettings,
  canAccessCompanySettings,
  canAccessFinancialSettings,
  canManageCompanyUsers,
} from '@/utils/userRoles';

// Logger para a página de configurações
const logger = getLogger('SETTINGS');

// Estado local da UI para esta página
export const settingsUIState = observable({
  isLoading: false
});

// Componente para cada item do menu de configurações
interface SettingsItemProps {
  icon: string;
  title: string;
  subtitle: string;
  route: string;
  onPress: () => void;
  locked?: boolean;
}

const iconMap: Record<string, string> = {
  'clipboard-list-outline': 'clipboard-list-outline',
  'person-outline': 'account-outline',
  'shield-account-outline': 'shield-account-outline',
  'business-outline': 'office-building-outline',
  'people-outline': 'account-group-outline',
  'card-outline': 'credit-card-outline',
  'cash-outline': 'cash-multiple',
  'developer': 'toolbox-outline',
  'tools': 'tools',
  'help-buoy-outline': 'lifebuoy',
};

const SettingsItem = ({ icon, title, subtitle, onPress, locked = false }: SettingsItemProps) => {
  return (
    <TouchableOpacity
      style={[styles.settingsItem, locked && styles.settingsItemLocked]}
      onPress={onPress}
      activeOpacity={locked ? 0.9 : 0.7}
    >
      <View style={styles.settingsItemIcon}>
        <MaterialCommunityIcons
          name={iconMap[icon] as any}
          size={24}
          color={theme.colors.primary.default}
        />
      </View>
      <View style={styles.settingsItemContent}>
        <Text style={styles.settingsItemTitle}>{title}</Text>
        <Text style={styles.settingsItemSubtitle}>{subtitle}</Text>
        {locked && (
          <View style={styles.lockBadge}>
            <MaterialCommunityIcons name="lock" size={14} color={theme.colors.primary.default} />
            <Text style={styles.lockBadgeText}>Plano Premium</Text>
          </View>
        )}
      </View>
      <MaterialCommunityIcons
        name="chevron-right"
        size={20}
        color={theme.colors.text.tertiary}
      />
    </TouchableOpacity>
  );
};

const SettingsScreen = observer(() => {
  const router = useRouter();
  const safeAreaInsets = useSafeAreaInsets();
  const headerPaddingTop = getSettingsHeaderPaddingTop(safeAreaInsets.top);
  const scrollViewBottomPadding = getSettingsContentPaddingBottom(safeAreaInsets.bottom);
  const userProfile = globalState.user.profile.get();
  const subscriptionSnapshot = globalState.subscription.get();
  const premiumAccess = hasPremiumAccess(subscriptionSnapshot, userProfile);
  const isDeveloper = hasDeveloperBypass(userProfile?.user_type);
  const canAccessCompanySection = canAccessCompanySettings(userProfile?.user_type);
  const canAccessChecklistSection = canAccessChecklistSettings(userProfile?.user_type);
  const canAccessUsersSection = canManageCompanyUsers(userProfile?.user_type);
  const canAccessFinancialSection = canAccessFinancialSettings(userProfile?.user_type);

  useEffect(() => {
    logger.debug('Página de configurações inicializada');
    return () => {
      logger.debug('Página de configurações desmontada');
    };
  }, []);

  // Funções de navegação para cada seção
  const navigateToChecklists = () => router.push('/settings/checklists');
  const navigateToAccount = () => router.push('/settings/account');
  const navigateToPrivacy = () => router.push('/settings/account?section=delete');
  const navigateToCompany = () => router.push('/settings/company');
  const navigateToSubscription = () => router.push('/settings/subscription');
  const navigateToPayments = () => router.push('/settings/payments');
  const navigateToAdvanced = () => router.push('/settings/advanced');
  const navigateToSupport = () => router.push('/settings/support');
  const handleRestrictedAccess = () => router.push('/settings/subscription/plans');
  const navigateToDeveloperPanel = () => router.push('/settings/developer');

  return (
    <View style={styles.container}>
      <View style={[styles.header, { paddingTop: headerPaddingTop }]}>
        <Text style={styles.headerTitle}>Configurações</Text>
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[styles.scrollViewContent, { paddingBottom: scrollViewBottomPadding }]}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.section}>
          {isDeveloper && (
            <SettingsItem
              icon="developer"
              title="Painel do Desenvolvedor"
              subtitle="Gerenciar assinaturas manuais e pagamentos PIX"
              route="/settings/developer"
              onPress={navigateToDeveloperPanel}
            />
          )}

          {canAccessChecklistSection && (
            <SettingsItem
              icon="clipboard-list-outline"
              title="Checklists"
              subtitle={premiumAccess ? 'Campos, grupos, ordenação' : 'Recurso exclusivo do plano Premium'}
              route="/settings/checklists"
              onPress={premiumAccess ? navigateToChecklists : handleRestrictedAccess}
              locked={!premiumAccess}
            />
          )}

          <SettingsItem
            icon="person-outline"
            title="Conta"
            subtitle="Dados pessoais, foto de perfil, telefone"
            route="/settings/account"
            onPress={navigateToAccount}
          />

          <SettingsItem
            icon="shield-account-outline"
            title="Privacidade e exclusão"
            subtitle="Termos, privacidade e excluir minha conta"
            route="/settings/account?section=delete"
            onPress={navigateToPrivacy}
          />

          {canAccessCompanySection && (
            <SettingsItem
              icon="business-outline"
              title="Empresa"
              subtitle="Dados da empresa, logo, endereço"
              route="/settings/company"
              onPress={navigateToCompany}
            />
          )}

          {canAccessUsersSection && (
            <SettingsItem
              icon="people-outline"
              title="Usuários"
              subtitle={premiumAccess ? 'Cadastrar e administrar usuários' : 'Disponível no plano Premium'}
              route="/settings/users"
              onPress={premiumAccess ? () => router.push('/settings/users') : handleRestrictedAccess}
              locked={!premiumAccess}
            />
          )}

          {canAccessFinancialSection && (
            <SettingsItem
              icon="card-outline"
              title="Assinatura"
              subtitle="Plano atual, renovação, status"
              route="/settings/subscription"
              onPress={navigateToSubscription}
            />
          )}

          {canAccessFinancialSection && (
            <SettingsItem
              icon="cash-outline"
              title="Pagamentos"
              subtitle={premiumAccess ? 'Histórico, métodos de pagamento' : 'Histórico disponível no plano Premium'}
              route="/settings/payments"
              onPress={premiumAccess ? navigateToPayments : handleRestrictedAccess}
              locked={!premiumAccess}
            />
          )}

          <SettingsItem
            icon="tools"
            title="Avançado"
            subtitle="Exportar dados, versão do aplicativo"
            route="/settings/advanced"
            onPress={navigateToAdvanced}
          />

        <SettingsItem
          icon="help-buoy-outline"
          title="Suporte"
          subtitle="Ajuda, contato, dúvidas frequentes"
          route="/settings/support"
          onPress={navigateToSupport}
        />
      </View>

      {!premiumAccess && (
        <UpgradePrompt variant="restricted" />
      )}
    </ScrollView>
  </View>
);
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.secondary,
  },
  header: {
    backgroundColor: theme.colors.primary.default,
    padding: theme.spacing.lg,
    paddingTop: Platform.OS === 'ios' ? theme.spacing.xl + theme.spacing.sm : theme.spacing.lg,
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: theme.fontSizes.xl,
    fontWeight: theme.fontWeights.bold as any,
    color: theme.colors.text.inverse,
  },
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    paddingBottom: theme.spacing.xl,
  },
  section: {
    backgroundColor: theme.colors.background.primary,
    margin: theme.spacing.md,
    borderRadius: theme.borderRadius.md,
    ...theme.shadows.md,
    overflow: 'hidden',
  },
  settingsItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border.light,
  },
  settingsItemLocked: {
    backgroundColor: theme.colors.background.tertiary,
  },
  settingsItemIcon: {
    width: 40,
    height: 40,
    borderRadius: 8,
    backgroundColor: `${theme.colors.primary.default}15`,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: theme.spacing.md,
  },
  settingsItemContent: {
    flex: 1,
  },
  settingsItemTitle: {
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.semibold as any,
    color: theme.colors.text.primary,
    marginBottom: 2,
  },
  settingsItemSubtitle: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.secondary,
  },
  lockBadge: {
    marginTop: theme.spacing.xs,
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xs,
  },
  lockBadgeText: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.primary.default,
    fontWeight: theme.fontWeights.medium as any,
  },
});

export default SettingsScreen; 
